import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import { useTheme } from '../../../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import { userIsMobileWorkstationOnly } from '../../../lib/operatorRoles';

const LoginForm = ({ language, onLoginSuccess, onSignUp }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { setTheme } = useTheme();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const { signIn } = useAuth();
    
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex?.test(email);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };


const handleSubmit = async (e) => {
  e?.preventDefault();
  
  const newErrors = {};
  
  if (!formData?.email) {
    newErrors.email = t('loginForm.emailRequired');
  } else if (!validateEmail(formData?.email)) {
    newErrors.email = t('loginForm.emailInvalid');
  }
  
  if (!formData?.password) {
    newErrors.password = t('loginForm.passwordRequired');
  }

  if (Object.keys(newErrors)?.length > 0) {
    setErrors(newErrors);
    return;
  }

  setLoading(true);
  setErrors({});

  try {
    const { data, error } = await signIn(formData.email, formData.password);

    if (error) {
      setErrors({ form: error.message || t('loginForm.invalidCredentials') });
      setLoading(false);
      return;
    }

    if (data?.user) {
      const { data: profileRow, error: profileError } = await supabase
        .from('user_profiles')
        .select('is_active')
        .eq('id', data.user.id)
        .maybeSingle();

      if (!profileError && profileRow && profileRow.is_active === false) {
        await supabase.auth.signOut();
        setErrors({ form: t('loginForm.accountInactive') });
        setLoading(false);
        return;
      }
    }

    if (data?.user) {
      localStorage.setItem('userEmail', data.user.email);
      localStorage.setItem('appLanguage', i18n.language || 'fr');
      
      if (formData?.rememberMe) {
        localStorage.setItem('rememberMe', 'true');
      } else {
        localStorage.removeItem('rememberMe');
      }

      if (onLoginSuccess) {
        onLoginSuccess(data.user);
      }

      const roleTypes = data.user?.roleTypes || [];
      const isOnlyDentistClient =
        roleTypes.length === 1 && roleTypes[0] === 'dentist_client';
      if (isOnlyDentistClient && !localStorage.getItem('appTheme')) {
        setTheme('neutral');
      }
      if (roleTypes.includes('administrator')) {
        navigate('/dashboard');
      } else if (isOnlyDentistClient) {
        navigate('/dentiste');
      } else if (userIsMobileWorkstationOnly(roleTypes)) {
        navigate('/atelier-lot');
      } else {
        navigate('/dashboard');
      }
    }
  } catch (error) {
    console.error('Login error:', error);
    setErrors({ form: t('loginForm.networkError') });
  } finally {
    setLoading(false);
  }
};
  return (
    <div className="w-full max-w-md">
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4" style={{ background: 'linear-gradient(135deg, var(--color-primary) 0%, var(--color-accent) 100%)' }}>
          <Icon name="Activity" size={32} color="#FFFFFF" />
        </div>
        <h1 className="text-3xl font-semibold text-foreground mb-2">{t('loginForm.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('loginForm.subtitle')}</p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-6">
        {errors?.form && (
          <div className="flex items-center gap-2 p-4 rounded-lg bg-error/10 border border-error/20">
            <Icon name="AlertCircle" size={20} className="text-error flex-shrink-0" />
            <p className="text-sm text-error">{errors?.form}</p>
          </div>
        )}

        <Input
          type="email"
          name="email"
          label={t('loginForm.email')}
          placeholder={t('loginForm.emailPlaceholder')}
          value={formData?.email}
          onChange={handleChange}
          error={errors?.email}
          required
          disabled={loading}
        />

        <div className="relative">
          <Input
            type={showPassword ? 'text' : 'password'}
            name="password"
            label={t('loginForm.password')}
            placeholder={t('loginForm.passwordPlaceholder')}
            value={formData?.password}
            onChange={handleChange}
            error={errors?.password}
            required
            disabled={loading}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-colors"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            <Icon style={{ marginTop: '-26px' }} name={showPassword ? 'EyeOff' : 'Eye'} size={20} />
          </button>
        </div>

        <div className="flex items-center justify-between">
          <Checkbox
            name="rememberMe"
            label={t('loginForm.rememberMe')}
            checked={formData?.rememberMe}
            onChange={handleChange}
            disabled={loading}
          />
          <button
            type="button"
            onClick={() => navigate('/forgot-password')}
            className="text-sm font-medium text-primary hover:text-primary/80 transition-colors"
          >
            {t('loginForm.forgotPassword')}
          </button>
        </div>

        <Button
          type="submit"
          variant="default"
          size="lg"
          fullWidth
          loading={loading}
          iconName="LogIn"
          iconPosition="right"
        >
         {t('loginForm.login')}
        </Button>

        {typeof onSignUp === 'function' ? (
          <Button
            type="button"
            variant="outline"
            size="lg"
            fullWidth
            disabled={loading}
            onClick={onSignUp}
          >
            {t('loginForm.signUp')}
          </Button>
        ) : null}
      </form>
    </div>
  );
};

export default LoginForm;